import React, { useCallback, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";

import Alert from "veneer/Alert";
import Autocomplete from "veneer/AutocompleteSelectInput";
import Box from "veneer/Box";
import Button from "veneer/Button";
import Drawer from "veneer/Drawer";
import Stack from "veneer/Stack";
import TextInput from "veneer/TextInput";
import Typography from "veneer/Typography";

import { MEDIA_LIBRARY_URL } from "cortex/ui/src/fleet/constant/mediaLibrary";
import {
  createFolder,
  moveMediaToFolder,
  removeMediaFromFolder,
} from "cortex/ui/src/fleet/view/media-library/action";
import { Media } from "cortex/ui/src/fleet/view/media-library/model";
import { getFolderUrl } from "cortex/ui/src/fleet/view/media-library/utils";
import { useAppDispatch } from "cortex/ui/src/hooks";

type Option = {
  label: string;
  value: number;
};

interface MoveToFolderDrawerProps {
  folderId: string | null;
  folders: ReadonlyArray<Media>;
  open: boolean;
  onCancel: () => void;
  selectedItems: ReadonlyArray<Media>;
  setSelectedItems: (items: ReadonlyArray<Media>) => void;
}

export default function CreateMediaGroupDrawer({
  folderId,
  folders,
  open,
  onCancel,
  selectedItems,
  setSelectedItems,
}: MoveToFolderDrawerProps) {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const [folderName, setFolderName] = useState<string>("");
  const [selectedFolder, setSelectedFolder] = useState<Option | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [folderCreated, setFolderCreated] = useState<boolean>(false);
  const alertActive = useMemo(() => {
    if (selectedItems.length === 0) {
      return { active: true, message: "No items selected to move" };
    }
    if (selectedItems.some((item) => item.type === "folder")) {
      return {
        active: true,
        message:
          "Cannot move folders to folder. Please deselect any folder from your selection.",
      };
    }
    return { active: false, message: "" };
  }, [selectedItems]);

  const handleCancel = useCallback(() => {
    setError(null);
    setFolderCreated(false);
    setFolderName("");
    setSelectedFolder(null);
    setSelectedItems([]);
    onCancel();
  }, [onCancel]);

  const validateForm = (
    action: "create" | "move",
    folderName: string,
    selectedFolder: Option | null,
  ) => {
    if (action === "create" && !folderName.trim()) {
      return "Folder name cannot be empty.";
    }
    if (action === "move" && !selectedFolder) {
      return "Please select a folder.";
    }
    return null;
  };

  const handleCreateFolder = useCallback(async () => {
    const validationError = validateForm("create", folderName, selectedFolder);
    if (validationError) {
      setError(validationError);
      return;
    }
    try {
      await dispatch(createFolder(folderName));
      setFolderName("");
      setFolderCreated(true);
    } catch (_err) {
      setError("Failed to create folder.");
      return;
    }
  }, [dispatch, folderName, handleCancel]);

  const handleMoveToFolder = useCallback(async () => {
    const validationError = validateForm("move", folderName, selectedFolder);
    if (validationError) {
      setError(validationError);
      return;
    }
    const folder = selectedFolder?.value;
    if (folder == null) {
      return;
    }
    try {
      await dispatch(moveMediaToFolder(folder, selectedItems));
      handleCancel();
      history.push(getFolderUrl(folder));
    } catch (_err) {
      setError("Failed to move items to folder. Please try again.");
      return;
    }
  }, [dispatch, folderName, handleCancel, selectedFolder, selectedItems]);

  const handleRemoveFromFolder = useCallback(async () => {
    try {
      await dispatch(removeMediaFromFolder(null, selectedItems));
      handleCancel();
      history.push(MEDIA_LIBRARY_URL);
    } catch (_err) {
      setError("Failed to remove items from folder. Please try again.");
      return;
    }
  }, [dispatch, handleCancel, selectedItems]);

  return (
    <Drawer
      commitDisabled={!!alertActive.active || !selectedFolder}
      commitLabel="Move"
      onCancel={handleCancel}
      onClose={handleCancel}
      onCommit={() => {
        handleMoveToFolder();
      }}
      onSecondary={() => {
        handleRemoveFromFolder();
      }}
      open={open}
      secondaryLabel={folderId ? "Remove from Folder" : undefined}
      size="large"
      title="Move File(s)"
    >
      <Box mb={2}>
        <Alert severity="info">
          Selected {selectedItems.length} item(s) to move.
        </Alert>
      </Box>
      {alertActive.active && (
        <Box mb={2}>
          <Alert severity="error">{alertActive.message}</Alert>
        </Box>
      )}
      {error && (
        <Box mb={2}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}
      {folderCreated && (
        <Box mb={2}>
          <Alert severity="success">
            Folder created successfully. Select the folder from the list to move
            items into it.
          </Alert>
        </Box>
      )}
      <Stack
        alignItems="center"
        direction="row"
        justifyContent="space-between"
        spacing={4}
      >
        <Stack direction="column" flexGrow={1} spacing={1}>
          <Typography variant="h5">Move file to existing folder</Typography>
          <Autocomplete
            disabled={!!alertActive.active}
            getOptionKey={(opt) => opt.value}
            inputProps={{ label: "Select Folder" }}
            isOptionEqualToValue={(opt: Option, value: Option) =>
              opt.value === value.value
            }
            onChange={(_e, opt: Option | null) => {
              setSelectedFolder(opt ?? null);
              setError(null);
            }}
            options={folders.map((folder) => ({
              label: folder.name,
              value: folder.id,
            }))}
            size="small"
            value={selectedFolder || null}
          />
        </Stack>
        <Typography>or</Typography>
        <Stack direction="column" flexGrow={1} spacing={1}>
          <Typography variant="h5">Add a new folder</Typography>
          <Stack direction="row" spacing={1}>
            <TextInput
              disabled={!!alertActive.active || !!selectedFolder}
              fullWidth
              label="Enter folder name"
              onChange={(newValue: string | null) => {
                setFolderName(newValue ?? "");
                setError(null);
              }}
              value={folderName}
            />
            <Button
              disabled={!!alertActive.active || !!selectedFolder}
              onClick={() => {
                handleCreateFolder();
              }}
            >
              Add Folder
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </Drawer>
  );
}
